import React from "react";
import {HistoryOutlined, DeleteOutlined} from "@ant-design/icons";
import {Input, InputNumber, InputSizes, Select, Switch, SwitchSizes, Text, TextAs, TextSizes, IconButton, SelectSizes} from "@clockout-dev/design-elements";
import ComposerSection from "./ComposerSection";
import RulesPanel from "../../common/rulesPanel/RulesPanel";
import EmptyState from "../../common/emptyState/EmptyState";
import {ATTEMPT_REASON_OPTIONS, ATTEMPT_STATUS_OPTIONS} from "../uploads-simulator.constants";
import {buildEmptyAttemptRow, MAX_ATTEMPT_ROWS} from "../sor.model";
import styles from "../styles/sor-composer.module.scss";

/**
 * SOR recent-intake-attempts section — the raw material for the
 * iterative-resubmission and accepted-after-repeated-rejections silent
 * signals. `minutes_ago` keeps the rows relative so presets never go stale.
 *
 * @param {Object} props
 * @param {Object[]} props.attempts — `sor.recent_intake_attempts`
 * @param {Record<string, string>} [props.errors]
 * @param {boolean} [props.filled]
 * @param {(attempts: Object[]) => void} props.onChange
 */
function SorAttemptHistorySection({attempts, errors, filled, onChange}) {
    const rows = Array.isArray(attempts) ? attempts : [];
    const setRow = (index, patch) => onChange(rows.map((row, rowIndex) => (rowIndex === index ? {...row, ...patch} : row)));

    return (
        <ComposerSection icon={<HistoryOutlined />} title="Recent Intake Attempts" filled={filled} defaultCollapsed>
            <RulesPanel
                onAdd={() => rows.length < MAX_ATTEMPT_ROWS && onChange([...rows, buildEmptyAttemptRow()])}
                addLabel={`Add an attempt (${rows.length}/${MAX_ATTEMPT_ROWS})`}
                errorMessage={errors?.recent_intake_attempts}
                emptyState={<EmptyState title="No prior attempts" description="Seed rejected attempts to demo iterative resubmission and accepted-after-repeated-rejections." />}>
                {rows.map((row, index) => (
                    <div key={index} className={styles.attemptRow}>
                        <div className={styles.attemptToggle}>
                            <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.toggleLabel}>
                                Accepted
                            </Text>
                            <Switch size={SwitchSizes.SMALL} checked={!!row.accepted} onChange={accepted => setRow(index, {accepted})} />
                        </div>
                        <Select
                            size={SelectSizes.SMALL}
                            label={index === 0 ? "Reason" : null}
                            allowClear
                            placeholder="Rejection reason"
                            options={ATTEMPT_REASON_OPTIONS}
                            value={row.reason || undefined}
                            onChange={reason => setRow(index, {reason: reason || ""})}
                        />
                        <InputNumber
                            label={index === 0 ? "Minutes ago" : null}
                            size={InputSizes.SMALL}
                            className={styles.numberField}
                            min={0}
                            status={errors?.[`recent_intake_attempts.${index}.minutes_ago`] ? "error" : ""}
                            value={row.minutes_ago}
                            onChange={minutesAgo => setRow(index, {minutes_ago: minutesAgo})}
                        />
                        <Select
                            size={SelectSizes.SMALL}
                            label={index === 0 ? "Status" : null}
                            options={ATTEMPT_STATUS_OPTIONS}
                            value={row.status || "completed"}
                            onChange={status => setRow(index, {status})}
                        />
                        <Input
                            label={index === 0 ? "Batch id" : null}
                            size={InputSizes.SMALL}
                            placeholder="optional"
                            value={row.batch_id || ""}
                            onChange={event => setRow(index, {batch_id: event.target.value})}
                        />
                        <IconButton
                            icon={<DeleteOutlined />}
                            className={styles.removeRowButton}
                            aria-label={`Remove attempt ${index + 1}`}
                            onClick={() => onChange(rows.filter((_, rowIndex) => rowIndex !== index))}
                        />
                    </div>
                ))}
            </RulesPanel>
        </ComposerSection>
    );
}

export default SorAttemptHistorySection;
